import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Table, Input, DropdownMenu, DropdownItem, UncontrolledDropdown, DropdownToggle, Row, Col, Button } from 'reactstrap';

import Loading from '../Global/Loading';
import Styles from './Styles/UserStyles';
import Detailed from './Detailed';
import { Breadcrumbs, Typography } from '@material-ui/core';
import dashboardConfig from '../../config';

import * as user_urls from '../../urls/api-users/admin';
import * as shipment_urls from '../../urls/api-shipments/admin';
import * as merchant_shipment_urls from '../../urls/api-shipments/merchant';
import DriverPartnerPanel from '../Drivers/DriverPartnerPanel';
import DriverBusinessAssignmentsPanel from '../Drivers/DriverBusinessAssignmentsPanel';
import DriverCompliancePanel from '../Drivers/DriverCompliancePanel';

const User = props => {
  const USER_SHIPMENTS_REQUEST_TIMEOUT_MS = 15000;
  const STORE_ACCESS_REQUEST_TIMEOUT_MS = 5000;
  const shouldSkipBrowserStoreAccessLookup = process.env.REACT_APP_ENABLE_BROWSER_STORE_ACCESS_LOOKUP !== 'true';
  let params = useParams();
  const location = useLocation();
  const userIdentifier = decodeURIComponent(params.userId || '');
  const storeContextId = decodeURIComponent(params.storeId || (location && location.state && location.state.storeId ? location.state.storeId : ''));
  const usersQueryHost = user_urls.hasuraHost;
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [user, setUser] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [firstName, setFirstName] = useState(null);
  const [lastName, setLastName] = useState(null);
  const [allShipments, setAllShipments] = useState([]);
  const [driverViewMode, setDriverViewMode] = useState('deliveries');
  const [auth0Activity, setAuth0Activity] = useState(null);
  const [auth0ActivityLoading, setAuth0ActivityLoading] = useState(false);
  const [passwordResetStatus, setPasswordResetStatus] = useState('');

  // Fetch the user's Auth0 account activity (last login) whenever the resolved user changes.
  const userAuth0IdForActivity = user && user.auth0_id ? user.auth0_id : null;
  useEffect(() => {
    if (!userAuth0IdForActivity) {
      setAuth0Activity(null);
      return undefined;
    }
    let cancelled = false;
    setAuth0ActivityLoading(true);
    setPasswordResetStatus('');
    axios({
      method: 'get',
      url: user_urls.userAuth0Activity(userAuth0IdForActivity),
      timeout: 10000,
      headers: { Authorization: 'Bearer ' + localStorage.getItem('access_token') }
    })
      .then(res => {
        if (cancelled) return;
        const payload = res && res.data && res.data.payload ? res.data.payload : res && res.data;
        setAuth0Activity(payload || null);
      })
      .catch(() => {
        if (!cancelled) setAuth0Activity(null);
      })
      .finally(() => {
        if (!cancelled) setAuth0ActivityLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userAuth0IdForActivity]);

  const sendPasswordReset = () => {
    const email = user && user.email ? String(user.email).trim() : '';
    if (!email) {
      setPasswordResetStatus('No email on file for this user.');
      return;
    }
    setPasswordResetStatus('Sending…');
    axios({
      method: 'POST',
      url: 'https://' + dashboardConfig.config.AUTH_CONFIG.domain + '/dbconnections/change_password',
      headers: { 'content-type': 'application/json' },
      data: {
        client_id: dashboardConfig.config.AUTH_CONFIG.clientId,
        email,
        connection: 'Username-Password-Authentication'
      }
    })
      .then(() => setPasswordResetStatus('Password reset email sent to ' + email))
      .catch(() => setPasswordResetStatus('Unable to send password reset right now.'));
  };
  const [profileDebugMeta, setProfileDebugMeta] = useState({
    lastShipmentSource: '',
    lastShipmentCount: 0,
    customerQuery: null,
    customerResponse: null,
    lastError: ''
  });

  const getShipmentSortTimestamp = shipment => {
    if (!shipment || typeof shipment !== 'object') {
      return 0;
    }

    const candidates = [shipment.completed_at, shipment.created_at, shipment.updated_at, shipment.scheduled_for];

    for (const candidate of candidates) {
      if (!candidate) {
        continue;
      }

      const timestamp = new Date(candidate).getTime();

      if (Number.isFinite(timestamp)) {
        return timestamp;
      }
    }

    return 0;
  };

  const sortShipmentsNewestFirst = candidateShipments => {
    if (!Array.isArray(candidateShipments)) {
      return [];
    }

    return [...candidateShipments].sort((leftShipment, rightShipment) => {
      const timestampDelta = getShipmentSortTimestamp(rightShipment) - getShipmentSortTimestamp(leftShipment);

      if (timestampDelta !== 0) {
        return timestampDelta;
      }

      const leftFriendlyId = Number(leftShipment && leftShipment.shipment_friendly_id);
      const rightFriendlyId = Number(rightShipment && rightShipment.shipment_friendly_id);

      if (Number.isFinite(leftFriendlyId) && Number.isFinite(rightFriendlyId) && rightFriendlyId !== leftFriendlyId) {
        return rightFriendlyId - leftFriendlyId;
      }

      return String(rightShipment && rightShipment.shipment_id ? rightShipment.shipment_id : '').localeCompare(
        String(leftShipment && leftShipment.shipment_id ? leftShipment.shipment_id : '')
      );
    });
  };

  const normalizeAuth0Candidate = value => {
    if (value == null) {
      return '';
    }

    const normalizedValue = String(value).trim();

    if (!normalizedValue || normalizedValue === 'null' || normalizedValue === 'undefined') {
      return '';
    }

    return normalizedValue;
  };

  const normalizeStoreIds = value => {
    const storeIds = Array.isArray(value) ? value : [value];

    return [...new Set(storeIds
      .map(storeId => (storeId == null ? '' : String(storeId).trim()))
      .filter(Boolean))];
  };

  const getPreferredAuthToken = () => localStorage.getItem('access_token') || localStorage.getItem('id_token') || '';

  const waitForStoreAccessRetry = timeoutMs => new Promise(resolve => {
    window.setTimeout(resolve, timeoutMs);
  });

  const fetchMerchantStoreAccessWithRetry = async currentUserAuth0Id => {
    const maxAttempts = 2;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await axios({
          method: 'get',
          url: user_urls.merchantStoreAccess(currentUserAuth0Id),
          timeout: STORE_ACCESS_REQUEST_TIMEOUT_MS,
          withCredentials: true,
          validateStatus: status => status === 200 || status === 400 || status === 401 || status === 403 || status === 404,
          headers: {
            'content-type': 'application/json',
            Authorization: 'Bearer ' + getPreferredAuthToken()
          }
        });

        if (!response || response.status !== 200) {
          return null;
        }

        const payload = response && response.data && response.data.payload ? response.data.payload : {};
        const storeIds = normalizeStoreIds([
          payload && payload.store_id,
          ...(payload && Array.isArray(payload.store_ids) ? payload.store_ids : []),
        ]);

        if (storeIds.length > 0) {
          return payload;
        }
      } catch (error) {
        if (attempt === maxAttempts) {
          throw error;
        }
      }

      if (attempt < maxAttempts) {
        await waitForStoreAccessRetry(250 * attempt);
      }
    }

    return null;
  };

  const resolveAccessibleStoreIds = async currentUser => {
    const fallbackStoreIds = normalizeStoreIds([
      storeContextId,
      currentUser && currentUser.store_id,
    ]);
    const currentUserAuth0Id = normalizeAuth0Candidate(currentUser && currentUser.auth0_id);

    if (shouldSkipBrowserStoreAccessLookup) {
      return fallbackStoreIds;
    }

    if (!currentUserAuth0Id || typeof user_urls.merchantStoreAccess !== 'function') {
      return fallbackStoreIds;
    }

    try {
      const payload = await fetchMerchantStoreAccessWithRetry(currentUserAuth0Id);
      return normalizeStoreIds([
        ...fallbackStoreIds,
        payload && payload.store_id,
        ...(payload && Array.isArray(payload.store_ids) ? payload.store_ids : []),
      ]);
    } catch (error) {
      return fallbackStoreIds;
    }
  };

  const handleLoadError = message => {
    setProfileDebugMeta(previous => ({
      ...previous,
      lastError: message || '',
      customerResponse: previous.customerResponse
        ? { ...previous.customerResponse, ok: false, error: message || 'unknown' }
        : previous.customerResponse,
    }));
    setLoadError(message);
    setLoading(false);
    setShipments([]);
    setAllShipments([]);
  };

  const applyLoadedShipments = (nextShipments, source = 'unknown') => {
    const sortedShipments = sortShipmentsNewestFirst(nextShipments);

    setProfileDebugMeta(previous => ({
      ...previous,
      lastShipmentSource: source,
      lastShipmentCount: sortedShipments.length,
      lastError: '',
    }));
    setLoadError('');
    setShipments(sortedShipments);
    setAllShipments(sortedShipments);
    setLoading(false);
  };

  const applyLoadedUser = currentUser => {
    const primaryRole = currentUser && Array.isArray(currentUser.Roles) && currentUser.Roles.length > 0
      ? currentUser.Roles[0]
      : null;

    setLoadError('');
    setUser(currentUser);
    setFirstName(currentUser.firstname || '');
    setLastName(currentUser.lastname || '');

    if (!primaryRole || typeof primaryRole.role !== 'number') {
      setShipments([]);
      setAllShipments([]);
      setLoading(false);
      setLoadError('This user profile is missing role information.');
      return;
    }

    if (storeContextId) {
      getStoreShipments(currentUser);
    } else if (primaryRole.role >= 400) {
      getCustomerShipments(currentUser);
    } else if (primaryRole.role >= 300) {
      getDriverShipments(currentUser);
    } else if (primaryRole.role >= 200) {
      getMerchantShipments(currentUser);
    } else if (primaryRole.role >= 0) {
      getAdminShipments(currentUser);
    } else {
      setShipments([]);
      setAllShipments([]);
      setLoading(false);
    }
  };

  const fetchFallbackDriverProfile = () => {
    const fallbackUrl = user_urls.dispatchDriversList([]);
    if (!fallbackUrl) {
      handleLoadError('Unable to load this user profile.');
      return;
    }

    axios({
      method: 'get',
      url: fallbackUrl,
      timeout: USER_SHIPMENTS_REQUEST_TIMEOUT_MS,
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
        Authorization: 'Bearer ' + getPreferredAuthToken()
      }
    }).then(driverResponse => {
      const payload = driverResponse && driverResponse.data && Array.isArray(driverResponse.data.payload)
        ? driverResponse.data.payload
        : [];
      const matchedDriver = payload.find(driver => {
        const driverEmail = driver && driver.User && driver.User.email ? String(driver.User.email).toLowerCase() : '';
        const normalizedIdentifier = String(userIdentifier || '').toLowerCase();

        return driver && (driver.auth0_id === userIdentifier || driverEmail === normalizedIdentifier);
      });

      if (!matchedDriver || !matchedDriver.User) {
        handleLoadError('Unable to load this user profile.');
        return;
      }

      applyLoadedUser({
        auth0_id: matchedDriver.auth0_id,
        username: matchedDriver.User.username || '',
        firstname: matchedDriver.User.firstname || '',
        lastname: matchedDriver.User.lastname || '',
        email: matchedDriver.User.email || '',
        phone: matchedDriver.phone || matchedDriver.User.phone || '',
        Addresses: Array.isArray(matchedDriver.User.Addresses) ? matchedDriver.User.Addresses : [],
        Roles: [{ role_description: 'driver', role: 300 }]
      });
    }).catch(() => {
      handleLoadError('Unable to load this user profile.');
    });
  };

  const applyStoreContextFallbackUser = () => {
    applyLoadedUser({
      auth0_id: userIdentifier,
      username: '',
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      Addresses: [],
      Roles: [{ role_description: 'super-merchant', role: 220 }]
    });
  };

  useEffect(() => {
    axios({
      method: `post`,
      url: usersQueryHost,
      timeout: USER_SHIPMENTS_REQUEST_TIMEOUT_MS,
      headers: {
        Authorization: 'Bearer ' + getPreferredAuthToken(),
        'content-type': 'application/json'
      },
      data: {
        query: `query LoadUserProfile($identifier: String!) {
          Users(
            where: {
              _or: [
                { auth0_id: { _eq: $identifier } }
                { email: { _eq: $identifier } }
              ]
            }
            limit: 1
          ) {
            auth0_id
            username
            firstname
            lastname
            email
            phone
            Addresses {
              address_1
              address_2
              city
              state
              country
              zip
            }
            Roles {
              role_description
              role
            }
          }
        }`,
        variables: {
          identifier: userIdentifier,
        }
      }
    }).then(response => {
      const users = response && response.data && response.data.data && Array.isArray(response.data.data.Users)
        ? response.data.data.Users
        : [];
      const currentUser = users.length > 0 ? users[0] : null;

      if (currentUser) {
        applyLoadedUser(currentUser);
      } else if (storeContextId) {
        applyStoreContextFallbackUser();
      } else {
        fetchFallbackDriverProfile();
      }
    }).catch(() => {
      if (storeContextId) {
        applyStoreContextFallbackUser();
      } else {
        fetchFallbackDriverProfile();
      }
    });
  }, [userIdentifier, storeContextId]);

  const getCustomerShipments = async user => {
    // This function returns every shipment tied to a
    // specific customer or their associated store scope.

    const auth0Id = normalizeAuth0Candidate(user && user.auth0_id);
    const userEmail = user && user.email ? String(user.email).trim() : '';
    const accessibleStoreIds = await resolveAccessibleStoreIds(user);

    setProfileDebugMeta(previous => ({
      ...previous,
      customerQuery: {
        auth0Id,
        userEmail,
        accessibleStoreIdsCount: accessibleStoreIds.length,
        accessibleStoreIds,
        requestHost: shipment_urls.hasuraHost,
      }
    }));

    axios({
      method: 'post',
      url: shipment_urls.hasuraHost,
      timeout: USER_SHIPMENTS_REQUEST_TIMEOUT_MS,
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
        'content-type': 'application/json'
      },
      data: {
        query: `query LoadCustomerShipments($auth0Id: String!, $userEmail: String!, $storeIds: [uuid!]!) {
                Shipments(
                  order_by: {updated_at: desc}
                  where: {
                    _or: [
                      {cust_auth0_id: {_eq: $auth0Id}}
                      {placed_by: {_eq: $auth0Id}}
                      {placed_by: {_eq: $userEmail}}
                      {Orders: {store_id: {_in: $storeIds}}}
                    ]
                  }
                ) {
                  shipment_id
                  is_expedited
                  num_of_gophrs
                  shipment_friendly_id
                  route_id
                  cust_auth0_id
                  customer_blob
                  distance
                  gratuity
                  shipment_total
                  shipping_fee
                  sum_of_orders_total
                  client_type
                  weight
                  created_at
                  updated_at
                  completed_at
                  scheduled_for
                  description
                  drop_off_instructions
                  pick_up_instructions
                  shipment_type
                  status
                  billing_status
                  billing_details
                  vehicle_type
                  is_roundtrip
                  merchant_order_id
                  shipping_fee_blob
                  placed_by
                  fedex_data
                  ai_recommendation
                  driver_1099_earning
                  passenger_1099_earning
                  DriverPays {
                    deleted_at
                    total_earning
                    driver_id
                    driver_status
                  }
                  ShipmentHandshakes {
                    url
                    type
                  }
                  Orders {
                    order_id
                    shipment_id
                    store_id
                    location_blob
                    order_friendly_id
                    actions
                    status
                    subtotal
                    sum_tax
                    total
                    created_at
                    updated_at
                    pick_up_instructions
                    description
                    weight
                    drop_off_instructions
                    estimated_arrival_time
                    completed_at
                    photos
                    actual_action
                  }
                  Route {
                    status
                    RouteDrivers {
                      distance
                      driver_id
                      driver_auth0_id
                      driver_blob
                      employment_type
                      tracking_blob
                      vehicle_blob
                      status
                    }
                    route_id
                    Shipments {
                      address_2
                      billed_route
                      created_at
                      cust_auth0_id
                      customer_blob
                      deleted_at
                      description
                      distance
                      distance_rate
                      drop_off_instructions
                      gratuity
                      is_expedited
                      pick_up_instructions
                      restricted_zone
                      restrictions
                      route_id
                      scheduled_for
                      shipment_total
                      shipping_fee
                      shipping_fee_blob
                      sum_of_orders_total
                      updated_at
                      updated_by
                      weight
                      sum_of_orders_total
                    }
                  }
                }
              }`,
        variables: {
          auth0Id,
          userEmail,
          storeIds: accessibleStoreIds,
        }
      }
    }).then(response => {
      if (response.data.data) {
        const loadedShipments = Array.isArray(response.data.data.Shipments) ? response.data.data.Shipments : [];

        setProfileDebugMeta(previous => ({
          ...previous,
          customerResponse: {
            ok: true,
            hasGraphData: true,
            shipmentCount: loadedShipments.length,
            statusBreakdown: loadedShipments.reduce((accumulator, shipmentRecord) => {
              const status = shipmentRecord && shipmentRecord.status ? shipmentRecord.status : 'unknown';
              accumulator[status] = (accumulator[status] || 0) + 1;
              return accumulator;
            }, {}),
          }
        }));
        applyLoadedShipments(loadedShipments, 'customer.primary');
      } else {
        setProfileDebugMeta(previous => ({
          ...previous,
          customerResponse: {
            ok: false,
            hasGraphData: false,
            shipmentCount: 0,
            statusBreakdown: {}
          }
        }));
        handleLoadError('Unable to load shipments for this customer.');
      }
    }).catch(error => {
      setProfileDebugMeta(previous => ({
        ...previous,
        customerResponse: {
          ok: false,
          hasGraphData: false,
          shipmentCount: 0,
          statusBreakdown: {},
          error: error && error.message ? error.message : 'request failed',
        }
      }));
      handleLoadError('Unable to load shipments for this customer.');
    });
  };

  const getDriverShipments = user => {
    // This function returns every shipment assigned
    // to a specific driver.  A shipment will be
    // displayed regardless of whether the user was
    // the driver or a passenger on it.

    axios({
      method: 'post',
      url: shipment_urls.hasuraHost,
      timeout: USER_SHIPMENTS_REQUEST_TIMEOUT_MS,
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
        'content-type': 'application/json'
      },
      data: {
        query:
          `
            {
                Shipments(order_by: {updated_at: desc}, where: {Route: {RouteDrivers: {driver_auth0_id: {_in: "` +
          user.auth0_id +
          `"}}}}) {
                  shipment_id
                  is_expedited
                  num_of_gophrs
                  shipment_friendly_id
                  route_id
                  cust_auth0_id
                  customer_blob
                  distance
                  gratuity
                  shipment_total
                  shipping_fee
                  sum_of_orders_total
                  client_type
                  weight
                  created_at
                  updated_at
                  completed_at
                  scheduled_for
                  description
                  drop_off_instructions
                  pick_up_instructions
                  shipment_type
                  status
                  billing_status
                  billing_details
                  vehicle_type
                  is_roundtrip
                  merchant_order_id
                  shipping_fee_blob
                  placed_by
                  fedex_data
                  ai_recommendation
                  driver_1099_earning
                  passenger_1099_earning
                  DriverPays {
                    deleted_at
                    total_earning
                    driver_id
                    driver_status
                  }
                  ShipmentHandshakes {
                    url
                    type
                  }
                  Orders {
                    order_id
                    shipment_id
                    store_id
                    location_blob
                    order_friendly_id
                    actions
                    status
                    subtotal
                    sum_tax
                    total
                    created_at
                    updated_at
                    pick_up_instructions
                    description
                    weight
                    drop_off_instructions
                    estimated_arrival_time
                    completed_at
                    photos
                    actual_action
                  }
                  Route {
                    status
                    RouteDrivers {
                      distance
                      driver_id
                      driver_auth0_id
                      driver_blob
                      employment_type
                      tracking_blob
                      vehicle_blob
                      status
                    }
                    route_id
                    Shipments {
                      address_2
                      billed_route
                      created_at
                      cust_auth0_id
                      customer_blob
                      deleted_at
                      description
                      distance
                      distance_rate
                      drop_off_instructions
                      gratuity
                      is_expedited
                      pick_up_instructions
                      restricted_zone
                      restrictions
                      route_id
                      scheduled_for
                      shipment_total
                      shipping_fee
                      shipping_fee_blob
                      sum_of_orders_total
                      updated_at
                      updated_by
                      weight
                    }
                  }
                }
              }`
      }
    }).then(response => {
      if (response.data.data) {
        applyLoadedShipments(response.data.data.Shipments);
      } else {
        handleLoadError('Unable to load shipments for this driver.');
      }
    }).catch(() => {
      handleLoadError('Unable to load shipments for this driver.');
    });
  };

  const getStoreShipments = async () => {
    const routeScopedStoreIds = normalizeStoreIds(storeContextId);
    const isLocalDebugMode = process.env.REACT_APP_ENV_MODE === 'local-debug';

    if (routeScopedStoreIds.length === 0) {
      handleLoadError('Unable to resolve store access for this user.');
      return;
    }

    const storeScopedQuery = `query LoadStoreShipmentsScoped($storeIds: [uuid!]!) {
      Shipments(
        order_by: {created_at: desc}
        limit: 2000
        where: {
          Orders: {store_id: {_in: $storeIds}}
        }
      ) {
        shipment_id
        is_expedited
        num_of_gophrs
        shipment_friendly_id
        route_id
        cust_auth0_id
        customer_blob
        distance
        shipment_total
        contains_alcohol
        shipping_fee
        sum_of_orders_total
        weight
        created_at
        updated_at
        completed_at
        scheduled_for
        description
        drop_off_instructions
        pick_up_instructions
        shipment_type
        status
        billing_status
        billing_details
        vehicle_type
        is_roundtrip
        merchant_order_id
        shipping_fee_blob
        placed_by
        fedex_data
        ai_recommendation
        Orders(order_by: { order_friendly_id: asc }) {
          order_id
          shipment_id
          store_id
          location_blob
          order_friendly_id
          actions
          status
          subtotal
          sum_tax
          total
          created_at
          updated_at
          pick_up_instructions
          description
          weight
          drop_off_instructions
          estimated_arrival_time
          completed_at
          photos
          actual_action
        }
        LocationUpdates {
          latitude
          longitude
          timestamp
        }
        ShipmentHandshakes {
          url
          type
        }
        Route {
          status
          RouteDrivers(where: { status: { _eq: "driver" }, deleted_at: { _is_null: true } }) {
            driver_blob
            driver_id
            vehicle_blob
            tracking_blob
          }
        }
      }
    }`;

    const applyIfNonEmpty = (candidateShipments, source) => {
      const normalizedShipments = Array.isArray(candidateShipments) ? candidateShipments : [];

      if (normalizedShipments.length > 0) {
        applyLoadedShipments(normalizedShipments, source);
        return true;
      }

      return false;
    };

    const merchantDashboardCandidateUrls = [
      merchant_shipment_urls.merchantDashboardShipments,
      merchant_shipment_urls.toHostedShipmentUrl(merchant_shipment_urls.merchantDashboardShipments),
    ].filter((url, index, urls) => Boolean(url) && urls.indexOf(url) === index);

    if (isLocalDebugMode) {
      for (const candidateUrl of merchantDashboardCandidateUrls) {
        try {
          const dashboardResponse = await axios({
            method: 'get',
            url: candidateUrl,
            timeout: USER_SHIPMENTS_REQUEST_TIMEOUT_MS,
            withCredentials: true,
            headers: {
              Authorization: 'Bearer ' + getPreferredAuthToken(),
              'content-type': 'application/json'
            }
          });

          const dashboardShipments = dashboardResponse && dashboardResponse.data && Array.isArray(dashboardResponse.data.payload)
            ? dashboardResponse.data.payload
            : [];

          const storeScopedShipments = dashboardShipments.filter(shipment => {
            if (!shipment || !Array.isArray(shipment.Orders)) {
              return false;
            }

            return shipment.Orders.some(order => order && order.store_id && routeScopedStoreIds.includes(String(order.store_id)));
          });

          if (storeScopedShipments.length > 0) {
            applyLoadedShipments(storeScopedShipments, `store.merchantDashboard.localDebug.filteredByStore:${candidateUrl}`);
            return;
          }

          if (dashboardShipments.length > 0) {
            applyLoadedShipments(dashboardShipments, `store.merchantDashboard.localDebug.unfiltered:${candidateUrl}`);
            return;
          }
        } catch (dashboardError) {
          void dashboardError;
        }
      }

      handleLoadError('Unable to load store-scoped shipments for this store.');
      return;
    }

    try {
      const localStoreScopeResponse = await axios({
        method: 'post',
        url: '/__local/shipments-hasura',
        timeout: USER_SHIPMENTS_REQUEST_TIMEOUT_MS,
        headers: {
          'content-type': 'application/json'
        },
        data: {
          query: storeScopedQuery,
          variables: {
            storeIds: routeScopedStoreIds,
          }
        }
      });

      if (localStoreScopeResponse && localStoreScopeResponse.data && localStoreScopeResponse.data.data) {
        if (applyIfNonEmpty(localStoreScopeResponse.data.data.Shipments, 'store.proxy')) {
          return;
        }
      }
    } catch (localStoreScopeError) {
      void localStoreScopeError;
    }

    const rawLocalSecret =
      (dashboardConfig
        && dashboardConfig.config
        && dashboardConfig.config.api_shipments
        && dashboardConfig.config.api_shipments.admin
        && dashboardConfig.config.api_shipments.admin.shipmentsHasuraAdminSecret)
      || (typeof process !== 'undefined' && process.env ? process.env.REACT_APP_SHIPMENTS_HASURA_ADMIN_SECRET : '')
      || '';
    const localShipmentsHasuraAdminSecret = String(rawLocalSecret).trim().replace(/^"|"$/g, '');
    const isLocalBrowser = typeof window !== 'undefined' && window.location && window.location.hostname === 'localhost';

    if (isLocalBrowser && localShipmentsHasuraAdminSecret) {
      try {
        const localDirectHasuraResponse = await axios({
          method: 'post',
          url: 'https://api-shipments.hasura.gophr.app/v1/graphql',
          timeout: USER_SHIPMENTS_REQUEST_TIMEOUT_MS,
          headers: {
            'content-type': 'application/json',
            'x-hasura-admin-secret': localShipmentsHasuraAdminSecret,
          },
          data: {
            query: storeScopedQuery,
            variables: {
              storeIds: routeScopedStoreIds,
            }
          }
        });

        if (localDirectHasuraResponse && localDirectHasuraResponse.data && localDirectHasuraResponse.data.data) {
          if (applyIfNonEmpty(localDirectHasuraResponse.data.data.Shipments, 'store.directAdminSecret')) {
            return;
          }
        }
      } catch (localDirectHasuraError) {
        void localDirectHasuraError;
      }
    }

    try {
      const bearerHasuraResponse = await axios({
        method: 'post',
        url: shipment_urls.hasuraHost,
        timeout: USER_SHIPMENTS_REQUEST_TIMEOUT_MS,
        headers: {
          Authorization: 'Bearer ' + getPreferredAuthToken(),
          'content-type': 'application/json'
        },
        data: {
          query: storeScopedQuery,
          variables: {
            storeIds: routeScopedStoreIds,
          }
        }
      });

      if (bearerHasuraResponse && bearerHasuraResponse.data && bearerHasuraResponse.data.data) {
        if (applyIfNonEmpty(bearerHasuraResponse.data.data.Shipments, 'store.bearerHasura')) {
          return;
        }
      }
    } catch (bearerHasuraError) {
      void bearerHasuraError;
    }

    for (const candidateUrl of merchantDashboardCandidateUrls) {
      try {
        const dashboardResponse = await axios({
          method: 'get',
          url: candidateUrl,
          timeout: USER_SHIPMENTS_REQUEST_TIMEOUT_MS,
          withCredentials: true,
          headers: {
            Authorization: 'Bearer ' + getPreferredAuthToken(),
            'content-type': 'application/json'
          }
        });

        const dashboardShipments = dashboardResponse && dashboardResponse.data && Array.isArray(dashboardResponse.data.payload)
          ? dashboardResponse.data.payload
          : [];

        const storeScopedShipments = dashboardShipments.filter(shipment => {
          if (!shipment || !Array.isArray(shipment.Orders)) {
            return false;
          }

          return shipment.Orders.some(order => order && order.store_id && routeScopedStoreIds.includes(String(order.store_id)));
        });

        if (storeScopedShipments.length > 0) {
          applyLoadedShipments(storeScopedShipments, `store.merchantDashboard.filteredByStore:${candidateUrl}`);
          return;
        }

        if (dashboardShipments.length > 0) {
          applyLoadedShipments(dashboardShipments, `store.merchantDashboard.unfiltered:${candidateUrl}`);
          return;
        }
      } catch (dashboardError) {
        void dashboardError;
      }
    }

    handleLoadError('Unable to load store-scoped shipments for this store.');
  };

  const getMerchantShipments = async u => {
    // This function returns all shipments containing
    // one or more orders from a store of which the
    // specified merchant is a store owner.

    let storeScopedShipments = [];
    const accessibleStoreIds = await resolveAccessibleStoreIds(u);

    if (accessibleStoreIds.length > 0) {
      try {
        const storeScopedResponse = await axios({
          method: 'post',
          url: shipment_urls.hasuraHost,
          timeout: USER_SHIPMENTS_REQUEST_TIMEOUT_MS,
          headers: {
            Authorization: 'Bearer ' + getPreferredAuthToken(),
            'content-type': 'application/json'
          },
          data: {
            query: `query LoadMerchantStoreScopedShipments($storeIds: [uuid!]!) {
              Shipments(order_by: {updated_at: desc}, where: {Orders: {store_id: {_in: $storeIds}}}) {
                shipment_id
                is_expedited
                num_of_gophrs
                shipment_friendly_id
                route_id
                cust_auth0_id
                customer_blob
                distance
                gratuity
                shipment_total
                shipping_fee
                sum_of_orders_total
                client_type
                weight
                created_at
                updated_at
                completed_at
                scheduled_for
                description
                drop_off_instructions
                pick_up_instructions
                shipment_type
                status
                billing_status
                billing_details
                vehicle_type
                is_roundtrip
                merchant_order_id
                shipping_fee_blob
                placed_by
                fedex_data
                ai_recommendation
                driver_1099_earning
                passenger_1099_earning
                DriverPays {
                  deleted_at
                  total_earning
                  driver_id
                  driver_status
                }
                ShipmentHandshakes {
                  url
                  type
                }
                Orders {
                  order_id
                  shipment_id
                  store_id
                  location_blob
                  order_friendly_id
                  actions
                  status
                  subtotal
                  sum_tax
                  total
                  created_at
                  updated_at
                  pick_up_instructions
                  description
                  weight
                  drop_off_instructions
                  estimated_arrival_time
                  completed_at
                  photos
                  actual_action
                }
                Route {
                  status
                  RouteDrivers {
                    distance
                    driver_id
                    driver_auth0_id
                    driver_blob
                    employment_type
                    tracking_blob
                    vehicle_blob
                    status
                  }
                  route_id
                  Shipments {
                    address_2
                    billed_route
                    created_at
                    cust_auth0_id
                    customer_blob
                    deleted_at
                    description
                    distance
                    distance_rate
                    drop_off_instructions
                    gratuity
                    is_expedited
                    pick_up_instructions
                    restricted_zone
                    restrictions
                    route_id
                    scheduled_for
                    shipment_total
                    shipping_fee
                    shipping_fee_blob
                    updated_at
                    updated_by
                    weight
                    sum_of_orders_total
                  }
                }
              }
            }`,
            variables: {
              storeIds: accessibleStoreIds,
            }
          }
        });

        if (storeScopedResponse && storeScopedResponse.data && storeScopedResponse.data.data) {
          storeScopedShipments = Array.isArray(storeScopedResponse.data.data.Shipments)
            ? storeScopedResponse.data.data.Shipments
            : [];
        }
      } catch (storeScopedError) {
        void storeScopedError;
      }
    }

    axios({
      method: 'post',
      url: shipment_urls.hasuraHost,
      timeout: USER_SHIPMENTS_REQUEST_TIMEOUT_MS,
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
        'content-type': 'application/json'
      },
      data: {
        query:
          `
            {
                Shipments(order_by: {updated_at: desc}, where: {Orders: {OrderStore: {StoreMerchants: {merchant_auth0_id: {_eq: "` +
          u.auth0_id +
          `"}}}}}) {
                  shipment_id
                  is_expedited
                  num_of_gophrs
                  shipment_friendly_id
                  route_id
                  cust_auth0_id
                  customer_blob
                  distance
                  gratuity
                  shipment_total
                  shipping_fee
                  sum_of_orders_total
                  client_type
                  weight
                  created_at
                  updated_at
                  completed_at
                  scheduled_for
                  description
                  drop_off_instructions
                  pick_up_instructions
                  shipment_type
                  status
                  billing_status
                  billing_details
                  vehicle_type
                  is_roundtrip
                  merchant_order_id
                  shipping_fee_blob
                  placed_by
                  fedex_data
                  ai_recommendation
                  driver_1099_earning
                  passenger_1099_earning
                  DriverPays {
                    deleted_at
                    total_earning
                    driver_id
                    driver_status
                  }
                  ShipmentHandshakes {
                    url
                    type
                  }
                  Orders {
                    order_id
                    shipment_id
                    store_id
                    location_blob
                    order_friendly_id
                    actions
                    status
                    subtotal
                    sum_tax
                    total
                    created_at
                    updated_at
                    pick_up_instructions
                    description
                    weight
                    drop_off_instructions
                    estimated_arrival_time
                    completed_at
                    photos
                    actual_action
                  }
                  Route {
                    status
                    RouteDrivers {
                      distance
                      driver_id
                      driver_auth0_id
                      driver_blob
                      employment_type
                      tracking_blob
                      vehicle_blob
                      status
                    }
                    route_id
                    Shipments {
                      address_2
                      billed_route
                      created_at
                      cust_auth0_id
                      customer_blob
                      deleted_at
                      description
                      distance
                      distance_rate
                      drop_off_instructions
                      gratuity
                      is_expedited
                      pick_up_instructions
                      restricted_zone
                      restrictions
                      route_id
                      scheduled_for
                      shipment_total
                      shipping_fee
                      shipping_fee_blob
                      updated_at
                      updated_by
                      weight
                      sum_of_orders_total
                    }
                  }
                }
              }
            `
      }
    }).then(response => {
      if (response.data.data) {
        const ownerScopedShipments = Array.isArray(response.data.data.Shipments)
          ? response.data.data.Shipments
          : [];
        const mergedShipmentsById = new Map();

        storeScopedShipments.forEach(shipmentRecord => {
          if (shipmentRecord && shipmentRecord.shipment_id) {
            mergedShipmentsById.set(shipmentRecord.shipment_id, shipmentRecord);
          }
        });
        ownerScopedShipments.forEach(shipmentRecord => {
          if (shipmentRecord && shipmentRecord.shipment_id) {
            mergedShipmentsById.set(shipmentRecord.shipment_id, shipmentRecord);
          }
        });

        applyLoadedShipments(Array.from(mergedShipmentsById.values()), 'merchant.mergedStoreAndOwnerScoped');
      } else {
        applyLoadedShipments(storeScopedShipments, 'merchant.accessibleStoresFallback');
      }
    }).catch(() => {
      if (storeScopedShipments.length > 0) {
        applyLoadedShipments(storeScopedShipments, 'merchant.accessibleStoresFallback');
      } else {
        handleLoadError('Unable to load shipments for this merchant.');
      }
    });
  };

  const getAdminShipments = u => {
    // This function returns all shipments which
    // a specific admin user has interacted with at
    // some point in its shipment lifecycle (assign,
    // cancel, complete, etc.)

    axios({
      method: 'post',
      url: shipment_urls.hasuraHost,
      timeout: USER_SHIPMENTS_REQUEST_TIMEOUT_MS,
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
        'content-type': 'application/json'
      },
      data: {
        query: `
            {
                Shipments(order_by: {updated_at: desc}, where: {ShipmentHistories: {actor: {_eq: "${u.auth0_id}"}}}) {
                  shipment_id
                  is_expedited
                  num_of_gophrs
                  shipment_friendly_id
                  route_id
                  cust_auth0_id
                  customer_blob
                  distance
                  gratuity
                  shipment_total
                  shipping_fee
                  sum_of_orders_total
                  client_type
                  weight
                  created_at
                  updated_at
                  completed_at
                  scheduled_for
                  description
                  drop_off_instructions
                  pick_up_instructions
                  shipment_type
                  status
                  billing_status
                  billing_details
                  vehicle_type
                  is_roundtrip
                  merchant_order_id
                  shipping_fee_blob
                  placed_by
                  fedex_data
                  ai_recommendation
                  driver_1099_earning
                  passenger_1099_earning
                  DriverPays {
                    deleted_at
                    total_earning
                    driver_id
                    driver_status
                  }
                  ShipmentHandshakes {
                    url
                    type
                  }
                  Orders {
                    order_id
                    shipment_id
                    store_id
                    location_blob
                    order_friendly_id
                    actions
                    status
                    subtotal
                    sum_tax
                    total
                    created_at
                    updated_at
                    pick_up_instructions
                    description
                    weight
                    drop_off_instructions
                    estimated_arrival_time
                    completed_at
                    photos
                    actual_action
                  }
                  Route {
                    status
                    RouteDrivers {
                      distance
                      driver_id
                      driver_auth0_id
                      driver_blob
                      employment_type
                      tracking_blob
                      vehicle_blob
                      status
                    }
                    route_id
                    Shipments {
                      address_2
                      billed_route
                      created_at
                      cust_auth0_id
                      customer_blob
                      deleted_at
                      description
                      distance
                      distance_rate
                      drop_off_instructions
                      gratuity
                      is_expedited
                      pick_up_instructions
                      restricted_zone
                      restrictions
                      route_id
                      scheduled_for
                      shipment_total
                      shipping_fee
                      shipping_fee_blob
                      sum_of_orders_total
                      updated_at
                      updated_by
                      weight
                      ShipmentHistories {
                        actor
                      }
                    }
                  }
                }
              }
            `
      }
    }).then(response => {
      if (response.data.data) {
        applyLoadedShipments(response.data.data.Shipments);
      } else {
        handleLoadError('Unable to load shipments for this admin.');
      }
    }).catch(() => {
      handleLoadError('Unable to load shipments for this admin.');
    });
  };

  // eslint-disable-next-line no-unused-vars
  const getAllShipments = () => {
    // This function isn't currently being used, but it
    // will be useful for a singular, fat-ass archive of
    // every shipment we have.  If we want it in a different
    // file, we can just copy pasta.

    axios({
      method: 'post',
      url: shipment_urls.hasuraHost,
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
        'content-type': 'application/json'
      },
      data: {
        query: `
            {
                Shipments(order_by: {updated_at: desc}) {
                  shipment_id
                  is_expedited
                  num_of_gophrs
                  shipment_friendly_id
                  route_id
                  cust_auth0_id
                  customer_blob
                  distance
                  gratuity
                  shipment_total
                  shipping_fee
                  sum_of_orders_total
                  client_type
                  weight
                  created_at
                  updated_at
                  completed_at
                  scheduled_for
                  description
                  drop_off_instructions
                  pick_up_instructions
                  shipment_type
                  status
                  billing_status
                  billing_details
                  vehicle_type
                  is_roundtrip
                  merchant_order_id
                  shipping_fee_blob
                  placed_by
                  fedex_data
                  ai_recommendation
                  Orders {
                    order_id
                    shipment_id
                    store_id
                    location_blob
                    order_friendly_id
                    actions
                    status
                    subtotal
                    sum_tax
                    total
                    created_at
                    updated_at
                    pick_up_instructions
                    description
                    weight
                    drop_off_instructions
                    estimated_arrival_time
                    completed_at
                    photos
                    actual_action
                  }
                  ShipmentHandshakes {
                    url
                    type
                  }
                  Route {
                    status
                    RouteDrivers {
                      distance
                      driver_id
                      driver_auth0_id
                      driver_blob
                      employment_type
                      tracking_blob
                      vehicle_blob
                      status
                    }
                    route_id
                    Shipments {
                      address_2
                      billed_route
                      created_at
                      cust_auth0_id
                      customer_blob
                      deleted_at
                      description
                      distance
                      distance_rate
                      drop_off_instructions
                      gratuity
                      is_expedited
                      pick_up_instructions
                      restricted_zone
                      restrictions
                      route_id
                      scheduled_for
                      shipment_total
                      shipping_fee
                      shipping_fee_blob
                      sum_of_orders_total
                      updated_at
                      updated_by
                      weight
                      ShipmentHistories {
                        actor
                      }
                    }
                  }
                }
              }
            `
      }
    }).then(response => {
      if (response.data.data) {
        applyLoadedShipments(response.data.data.Shipments);
      } else {
        handleLoadError('Unable to load shipment history.');
      }
    }).catch(() => {
      handleLoadError('Unable to load shipment history.');
    });
  };

  const handleSearchChange = event => {
    const val = event.target.value;
    const sourceShipments = Array.isArray(allShipments) ? allShipments : [];
    const filtered = [];

    if (event.target.value === '') {
      setShipments(sortShipmentsNewestFirst(sourceShipments));
    } else {
      sourceShipments.forEach(element => {
        const customer = element.customer_blob || {};
        const name = [customer.first_name, customer.last_name].filter(Boolean).join(' ');
        const completedAt = element.completed_at || '';

        if (
          element.shipment_friendly_id
            .toString()
            .toLowerCase()
            .includes(val.toLowerCase()) ||
          element.shipping_fee
            .toString()
            .toLowerCase()
            .includes(val.toLowerCase()) ||
          name.toLowerCase().includes(val.toLowerCase()) ||
          element.status.toLowerCase().includes(val.toLowerCase()) ||
          completedAt.toLowerCase().includes(val.toLowerCase())
        ) {
          filtered.push(element);
        }
      });
      setShipments(sortShipmentsNewestFirst(filtered));
    }
    // console.log("Inside search", val);
    // console.log("Inside search", filtered);
  };

  const handleStatusFilter = selectedStatus => {
    const sourceShipments = Array.isArray(allShipments) ? allShipments : [];
    const filtered = [];

    if (selectedStatus === 'all') {
      setShipments(sortShipmentsNewestFirst(sourceShipments));
    } else {
      sourceShipments.forEach(element => {
        if (element.status === selectedStatus) {
          filtered.push(element);
        }
      });
      setShipments(sortShipmentsNewestFirst(filtered));
    }
  };

  // const handleResetShipment = () => {
  //   setShipments(allShipments);
  // }

  if (loading) return <Loading />;
  const visibleShipments = Array.isArray(shipments) ? shipments : [];
  const isLocalDebugHost = typeof window !== 'undefined' && window.location && window.location.hostname === 'localhost';
  if (!user) {
    return (
      <Styles className="user-profile-wrapper">
        <div className="user-profile-header-left">
          <div className="user-info">
            {loadError ? <p style={{ color: '#ff6b6b', fontSize: 14, paddingBottom: 10 }}>{loadError}</p> : null}
            <Breadcrumbs aria-label="breadcrumb">
              <Link to="/admin/users" style={{ color: '#5e72e4' }}>
                Users
              </Link>

              <Typography style={{ color: '#5e72e4', fontWeight: 'bold' }}>Unavailable</Typography>
            </Breadcrumbs>
            <h2>User Info</h2>
            <p>This user profile could not be loaded.</p>
          </div>
        </div>
      </Styles>
    );
  }
  const primaryRole = user && Array.isArray(user.Roles) && user.Roles.length > 0 ? user.Roles[0] : null;
  const roleDescription = primaryRole && primaryRole.role_description ? primaryRole.role_description : 'unknown';
  const showCustomerDebugPanel = isLocalDebugHost && roleDescription === 'customer';
  const userFirstName = user.firstname || '';
  const userLastName = user.lastname || '';
  const userFullName = [userFirstName, userLastName].filter(Boolean).join(' ') || 'N/A';
  const userUsername = user.username || 'N/A';
  const userEmail = user.email || 'N/A';
  const userPhone = user.phone || 'N/A';
  const userAddress = user.address_1
    ? `${user.address_1} ${user.address_2 || ''}, ${user.city || ''} ${user.state || ''}, ${user.zip || ''}`.replace(/\s+/g, ' ').trim()
    : '';
  const isDriverProfile = roleDescription === 'driver';
  const getDriverToggleStyle = isActive => ({
    minWidth: '140px',
    border: isActive ? '1px solid #2bbbae' : '1px solid rgba(255, 255, 255, 0.28)',
    background: isActive ? 'rgba(43, 187, 174, 0.18)' : 'rgba(255, 255, 255, 0.08)',
    color: isActive ? '#ffffff' : 'rgba(255, 255, 255, 0.86)',
    fontWeight: 600,
  });
  return (
    <Styles className="user-profile-wrapper">
      <div className="user-profile-header-left">
        <div className="user-info">
          {isDriverProfile ? (
            <div style={{ marginBottom: '20px' }}>
              <DriverPartnerPanel driverAuth0Id={user.auth0_id} />
            </div>
          ) : null}
          {loadError ? <p style={{ color: '#ff6b6b', fontSize: 14, paddingBottom: 10 }}>{loadError}</p> : null}
          {/* Breadcrumbs */}
          <Breadcrumbs aria-label="breadcrumb">
            <Link to="/admin/users" style={{ color: '#5e72e4' }}>
              Users
            </Link>

            <Typography style={{ color: '#5e72e4', fontWeight: 'bold' }}>
              {firstName} {lastName}
            </Typography>
          </Breadcrumbs>
          {/* End Breadcrumbs */}
          <h2>User Info</h2>
          <p>
            <strong>First & Last Name: </strong>
            {userFullName}
          </p>
          <p>
            <strong>Role: </strong>
            {roleDescription}
          </p>
          <p>
            <strong>Username: </strong>
            {userUsername}
          </p>
          <p>
            <strong>Email: </strong>
            {userEmail}
          </p>
          <p>
            <strong>Last Login: </strong>
            {auth0ActivityLoading
              ? 'Loading…'
              : auth0Activity && auth0Activity.last_login
                ? new Date(auth0Activity.last_login).toLocaleString()
                  + (auth0Activity.logins_count ? ' · ' + auth0Activity.logins_count + ' logins' : '')
                : 'Never'}
          </p>
          <div style={{ marginTop: '6px', marginBottom: '12px' }}>
            <Button
              size="sm"
              color="warning"
              onClick={sendPasswordReset}
              disabled={!(user && user.email)}
              style={{ margin: 0 }}
            >
              Send Password Reset
            </Button>
            {passwordResetStatus ? (
              <span style={{ marginLeft: '10px', fontSize: '13px' }}>{passwordResetStatus}</span>
            ) : null}
          </div>
          <p>
            <strong>Primary Phone: </strong>
            {/* Wanting to come back to phone number formatting for all display instances */}
            {/* {formatPhone(user.phone)} */}
            {userPhone}
          </p>
          {userAddress ? (
            <p>
              <strong>Primary Address: </strong>
              {userAddress}
            </p>
          ) : null}
          {showCustomerDebugPanel ? (
            <div
              style={{
                marginTop: '12px',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid rgba(94, 114, 228, 0.35)',
                background: 'rgba(94, 114, 228, 0.10)',
                fontSize: '12px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              <strong>Customer View Debug</strong>
              <pre style={{ marginTop: '8px', marginBottom: 0, color: '#dbe2ff' }}>
                {JSON.stringify({
                  userIdentifier,
                  storeContextId,
                  roleDescription,
                  loading,
                  loadError,
                  visibleShipmentsCount: visibleShipments.length,
                  allShipmentsCount: Array.isArray(allShipments) ? allShipments.length : 0,
                  shipmentStatusBreakdown: (Array.isArray(allShipments) ? allShipments : []).reduce((accumulator, shipmentRecord) => {
                    const status = shipmentRecord && shipmentRecord.status ? shipmentRecord.status : 'unknown';
                    accumulator[status] = (accumulator[status] || 0) + 1;
                    return accumulator;
                  }, {}),
                  profileDebugMeta,
                }, null, 2)}
              </pre>
            </div>
          ) : null}
          {isDriverProfile ? (
            <div className="driver-actions-panel">
              <DriverCompliancePanel driverAuth0Id={user.auth0_id} />
            </div>
          ) : null}
        </div>
      </div>
      <div className="user-orders-right">
        {roleDescription === 'super-admin' ? (
          null
        ) : roleDescription === 'admin' ? (
          <h2>Total Orders</h2>
        ) : roleDescription === 'super-merchant' ? (
          <h2>{`${userFullName} - Total Orders`}</h2>
        ) : roleDescription === 'driver' ? (
          <h2>{driverViewMode === 'businesses' ? `${userFullName} - Businesses` : `${userFullName} - Completed Orders`}</h2>
        ) : roleDescription === 'customer' ? (
          <h2>{`${userFullName} - Total Orders`}</h2>
        ) : roleDescription === 'inventory_manager' ? (
          <h2>{`${userFullName} - Total Orders`}</h2>
        ) : null}
        {isDriverProfile ? (
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
            <Button
              className={driverViewMode === 'deliveries' ? 'btn btn-default' : 'btn btn-simple'}
              style={getDriverToggleStyle(driverViewMode === 'deliveries')}
              onClick={() => setDriverViewMode('deliveries')}
            >
              Deliveries
            </Button>
            <Button
              className={driverViewMode === 'businesses' ? 'btn btn-default' : 'btn btn-simple'}
              style={getDriverToggleStyle(driverViewMode === 'businesses')}
              onClick={() => setDriverViewMode('businesses')}
            >
              Businesses
            </Button>
          </div>
        ) : null}

        {isDriverProfile && driverViewMode === 'businesses' ? (
          <DriverBusinessAssignmentsPanel driverAuth0Id={user.auth0_id} />
        ) : (
          <>
            <Row>
              <Col xs="10">
                <Input placeholder="Search here..." onChange={handleSearchChange}></Input>
              </Col>
              <Col>
                <UncontrolledDropdown style={{ marginRight: 20, marginTop: 5 }}>
                  <DropdownToggle
                    caret
                    className="btn-link btn-icon"
                    color="default"
                    data-toggle="dropdown"
                    type="button"
                  >
                    <i className="fas fa-filter"></i>
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem onClick={() => handleStatusFilter('all')}>All</DropdownItem>
                    <DropdownItem onClick={() => handleStatusFilter('shipment_created')}>Created</DropdownItem>
                    <DropdownItem onClick={() => handleStatusFilter('shipment_ready_for_driver')}>Ready For Driver</DropdownItem>
                    <DropdownItem onClick={() => handleStatusFilter('shipment_accepted')}>Accepted</DropdownItem>
                    <DropdownItem onClick={() => handleStatusFilter('shipment_assigned')}>Assigned</DropdownItem>
                    <DropdownItem onClick={() => handleStatusFilter('shipment_completed')}>Completed</DropdownItem>
                    <DropdownItem onClick={() => handleStatusFilter('shipment_saved')}>Saved</DropdownItem>
                    <DropdownItem onClick={() => handleStatusFilter('shipment_declined')}>Declined</DropdownItem>
                    <DropdownItem onClick={() => handleStatusFilter('shipment_cancelled')}>Cancelled</DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </Col>
            </Row>

            <div style={{ overflowX: 'auto' }}>
              <Table style={{ cursor: 'pointer', tableLayout: 'fixed' }}>
                <thead className="text-primary">
                  <tr>
                    <th style={{ width: '25%' }}>Order Placed By</th>
                    <th style={{ width: '15%' }}>ID</th>
                    <th style={{ width: '18%' }}>Completed At</th>
                    <th style={{ width: '18%' }}>Status</th>
                    <th style={{ width: '15%' }}>Delivery Fee</th>
                    <th style={{ width: '9%' }}>Invoice Status</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleShipments.map(s => {
                    return <Detailed key={s.shipment_friendly_id || s.shipment_id || s.id} shipment={s} user={user} disableInvoiceStatusFetch={true} />;
                  })}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </div>
    </Styles>
  );
};

export default User;
